import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include user role and ID in headers
api.interceptors.request.use(
  (config) => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        if (user.role) {
          config.headers['userRole'] = user.role;
        }
        if (user.id) {
          config.headers['userId'] = user.id;
        }
      } catch (error) {
        console.error('Error parsing user from localStorage:', error);
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle errors properly
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Extract error message from response
    const errorMessage = error.response?.data?.error || error.message || 'An unexpected error occurred';

    // If there are missing prices, include them in the error message
    if (error.response?.data?.missingPrices) {
      const missingItems = error.response.data.missingPrices.join(', ');
      throw new Error(`${errorMessage}\n\nMissing prices for: ${missingItems}`);
    }

    // Throw error with proper message
    throw new Error(errorMessage);
  }
);

// Health Check
export const healthCheck = async () => {
  const response = await api.get('/health');
  return response.data;
};

// Auth APIs
export const login = async (username, password) => {
  const response = await api.post('/auth/login', { username, password });
  return response.data;
};

export const logout = async () => {
  const response = await api.post('/auth/logout');
  return response.data;
};

// User APIs
export const getUsers = async () => {
  const response = await api.get('/users');
  return response.data;
};

export const getUser = async (id) => {
  const response = await api.get(`/users/${id}`);
  return response.data;
};

export const createUser = async (userData) => {
  const response = await api.post('/users', userData);
  return response.data;
};

export const updateUser = async (id, userData) => {
  const response = await api.put(`/users/${id}`, userData);
  return response.data;
};

export const deleteUser = async (id) => {
  const response = await api.delete(`/users/${id}`);
  return response.data;
};

export const clearUserConversation = async (id) => {
  const response = await api.post(`/users/${id}/clear-conversation`);
  return response.data;
};

// Customer APIs
export const getCustomers = async () => {
  const response = await api.get('/customers');
  return response.data;
};

export const getCustomer = async (id) => {
  const response = await api.get(`/customers/${id}`);
  return response.data;
};

export const createCustomer = async (customerData) => {
  const response = await api.post('/customers', customerData);
  return response.data;
};

export const updateCustomer = async (id, customerData) => {
  const response = await api.put(`/customers/${id}`, customerData);
  return response.data;
};

export const deleteCustomer = async (id) => {
  const response = await api.delete(`/customers/${id}`);
  return response.data;
};

// Site APIs
export const getSites = async (customerId = null) => {
  const params = customerId ? { customerId } : {};
  const response = await api.get('/sites', { params });
  return response.data;
};

export const getSite = async (id) => {
  const response = await api.get(`/sites/${id}`);
  return response.data;
};

export const createSite = async (siteData) => {
  const response = await api.post('/sites', siteData);
  return response.data;
};

export const updateSite = async (id, siteData) => {
  const response = await api.put(`/sites/${id}`, siteData);
  return response.data;
};

export const deleteSite = async (id) => {
  const response = await api.delete(`/sites/${id}`);
  return response.data;
};

// Inquiry APIs
export const getInquiries = async (filters = {}) => {
  const response = await api.get('/inquiries', { params: filters });
  return response.data;
};

export const getInquiry = async (id) => {
  const response = await api.get(`/inquiries/${id}`);
  return response.data;
};

export const createInquiry = async (inquiryData) => {
  const response = await api.post('/inquiries', inquiryData);
  return response.data;
};

export const updateInquiry = async (id, inquiryData) => {
  const response = await api.put(`/inquiries/${id}`, inquiryData);
  return response.data;
};

export const deleteInquiry = async (id) => {
  const response = await api.delete(`/inquiries/${id}`);
  return response.data;
};

export const createSubInquiry = async (parentInquiryId, items) => {
  const response = await api.post(`/inquiries/${parentInquiryId}/partial-conversion`, { items });
  return response.data;
};

// Quotation APIs
export const getQuotations = async (filters = {}) => {
  const response = await api.get('/quotations', { params: filters });
  return response.data;
};

export const getQuotation = async (id) => {
  const response = await api.get(`/quotations/${id}`);
  return response.data;
};

export const createQuotation = async (quotationData) => {
  const response = await api.post('/quotations', quotationData);
  return response.data;
};

export const updateQuotation = async (id, quotationData) => {
  const response = await api.put(`/quotations/${id}`, quotationData);
  return response.data;
};

export const deleteQuotation = async (id) => {
  const response = await api.delete(`/quotations/${id}`);
  return response.data;
};

// Order APIs
export const getOrders = async (filters = {}) => {
  const response = await api.get('/orders', { params: filters });
  return response.data;
};

export const getOrder = async (id) => {
  const response = await api.get(`/orders/${id}`);
  return response.data;
};

export const createOrderFromQuotation = async (quotationId, orderData) => {
  const response = await api.post(`/orders/from-quotation/${quotationId}`, orderData);
  return response.data;
};

export const createOrder = async (orderData) => {
  const response = await api.post('/orders', orderData);
  return response.data;
};

export const updateOrder = async (id, orderData) => {
  const response = await api.put(`/orders/${id}`, orderData);
  return response.data;
};

export const updateOrderStatus = async (id, status, deliveryInfo = {}) => {
  const response = await api.patch(`/orders/${id}/status`, { status, ...deliveryInfo });
  return response.data;
};

export const updateOrderItemStatus = async (orderId, itemId, status) => {
  const response = await api.patch(`/orders/${orderId}/items/${itemId}/status`, { status });
  return response.data;
};

export const createPartialDispatch = async (orderId, dispatchData) => {
  const response = await api.post(`/orders/${orderId}/partial-dispatch`, dispatchData);
  return response.data;
};

export const cancelOrder = async (id) => {
  const response = await api.delete(`/orders/${id}`);
  return response.data;
};

// Delivery APIs
export const getDeliveries = async (filters = {}) => {
  const response = await api.get('/deliveries', { params: filters });
  return response.data;
};

export const getDelivery = async (id) => {
  const response = await api.get(`/deliveries/${id}`);
  return response.data;
};

export const createDelivery = async (deliveryData) => {
  const response = await api.post('/deliveries', deliveryData);
  return response.data;
};

export const updateDelivery = async (id, deliveryData) => {
  const response = await api.put(`/deliveries/${id}`, deliveryData);
  return response.data;
};

export const cancelDelivery = async (id) => {
  const response = await api.delete(`/deliveries/${id}`);
  return response.data;
};

// Invoice APIs
export const getInvoices = async (filters = {}) => {
  const response = await api.get('/invoices', { params: filters });
  return response.data;
};

export const getInvoice = async (id) => {
  const response = await api.get(`/invoices/${id}`);
  return response.data;
};

export const createInvoiceFromOrder = async (orderId, invoiceData) => {
  const response = await api.post(`/invoices/from-order/${orderId}`, invoiceData);
  return response.data;
};

export const createInvoice = async (invoiceData) => {
  const response = await api.post('/invoices', invoiceData);
  return response.data;
};

export const updateInvoice = async (id, invoiceData) => {
  const response = await api.put(`/invoices/${id}`, invoiceData);
  return response.data;
};

export const cancelInvoice = async (id) => {
  const response = await api.delete(`/invoices/${id}`);
  return response.data;
};

// Sample APIs
export const getSamples = async (filters = {}) => {
  const response = await api.get('/samples', { params: filters });
  return response.data;
};

export const getSample = async (id) => {
  const response = await api.get(`/samples/${id}`);
  return response.data;
};

export const createSample = async (sampleData) => {
  const response = await api.post('/samples', sampleData);
  return response.data;
};

export const returnSample = async (id, returnData) => {
  const response = await api.put(`/samples/${id}/return`, returnData);
  return response.data;
};

export const updateSample = async (id, sampleData) => {
  const response = await api.put(`/samples/${id}`, sampleData);
  return response.data;
};

export const deleteSample = async (id) => {
  const response = await api.delete(`/samples/${id}`);
  return response.data;
};

// Followup APIs
export const getFollowups = async (filters = {}) => {
  const response = await api.get('/followups', { params: filters });
  return response.data;
};

export const getFollowup = async (id) => {
  const response = await api.get(`/followups/${id}`);
  return response.data;
};

export const createFollowup = async (followupData) => {
  const response = await api.post('/followups', followupData);
  return response.data;
};

export const updateFollowup = async (id, followupData) => {
  const response = await api.put(`/followups/${id}`, followupData);
  return response.data;
};

export const deleteFollowup = async (id) => {
  const response = await api.delete(`/followups/${id}`);
  return response.data;
};

// Comments API
export const getComments = async (referenceType, referenceId) => {
  const response = await api.get(`/comments?relatedTo=${referenceType}&relatedId=${referenceId}`);
  // Map backend field names to frontend field names
  return response.data.map(comment => ({
    id: comment.id,
    comment: comment.text,
    userName: comment.user_name || 'Unknown User',
    createdAt: comment.created_at,
    relatedTo: comment.related_to,
    relatedId: comment.related_id,
    createdBy: comment.created_by
  }));
};

export const createComment = async (commentData) => {
  // Map frontend field names to backend field names
  const backendData = {
    relatedTo: commentData.referenceType,
    relatedId: commentData.referenceId,
    text: commentData.comment,
    createdBy: commentData.userId
  };
  const response = await api.post('/comments', backendData);
  return response.data;
};

export const updateComment = async (id, commentData) => {
  const response = await api.put(`/comments/${id}`, commentData);
  return response.data;
};

export const deleteComment = async (id) => {
  const response = await api.delete(`/comments/${id}`);
  return response.data;
};

// Edit History API
export const getEditHistory = async (tableName, recordId) => {
  const response = await api.get(`/edit-history/${tableName}/${recordId}`);
  return response.data;
};

export default api;
